import React, { useEffect, useRef, useState } from "react";

// Simple Arabic letter tracing game using Canvas
// Users can pick a letter and trace over a faint guide. Works with mouse/touch.
function TracingGame() {
  const canvasRef = useRef(null);
  const guideRef = useRef(null);
  const [selectedLetter, setSelectedLetter] = useState("ا");
  const [isDrawing, setIsDrawing] = useState(false);
  const [strokeColor, setStrokeColor] = useState("#1d4ed8");
  const [strokeWidth, setStrokeWidth] = useState(10);

  const letters = [
    "ا","ب","ت","ث","ج","ح","خ","د","ذ","ر","ز","س","ش","ص","ض","ط","ظ","ع","غ","ف","ق","ك","ل","م","ن","هـ","و","ي"
  ];

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const guide = guideRef.current;
    const gtx = guide.getContext("2d");

    const applyHiDPI = (cnv, context, cssW, cssH) => {
      const ratio = window.devicePixelRatio || 1;
      cnv.style.width = `${cssW}px`;
      cnv.style.height = `${cssH}px`;
      cnv.width = Math.floor(cssW * ratio);
      cnv.height = Math.floor(cssH * ratio);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
    };

    // Size canvases to container with HiDPI support
    const resize = () => {
      const parent = canvas.parentElement;
      const cssWidth = Math.min(parent.clientWidth, 600);
      const cssHeight = Math.min(500, Math.floor(cssWidth * 0.8));
      applyHiDPI(guide, gtx, cssWidth, cssHeight);
      applyHiDPI(canvas, ctx, cssWidth, cssHeight);
      drawGuide(gtx, cssWidth, cssHeight);
      clearCanvas(ctx, cssWidth, cssHeight);
    };

    const drawGuide = (gtx, w, h) => {
      gtx.clearRect(0, 0, w, h);
      // Background
      gtx.fillStyle = "#fff";
      gtx.fillRect(0, 0, w, h);
      // Grid lines
      gtx.strokeStyle = "#e5e7eb";
      gtx.lineWidth = 1;
      for (let x = 0; x <= w; x += 40) {
        gtx.beginPath(); gtx.moveTo(x, 0); gtx.lineTo(x, h); gtx.stroke();
      }
      for (let y = 0; y <= h; y += 40) {
        gtx.beginPath(); gtx.moveTo(0, y); gtx.lineTo(w, y); gtx.stroke();
      }
      // Letter guide (no flip so drawing aligns visually)
      gtx.font = `${Math.floor(h * 0.7)}px Arial`;
      gtx.textAlign = "center";
      gtx.textBaseline = "middle";
      gtx.fillStyle = "rgba(16, 185, 129, 0.15)"; // green-500 @ 15%
      gtx.fillText(selectedLetter, w / 2, h / 2);
    };

    const clearCanvas = (ctx, w, h) => {
      ctx.clearRect(0, 0, w, h);
    };

    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [selectedLetter]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    let lastX = 0, lastY = 0;

    const getPos = (e) => {
      const rect = canvas.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      return {
        x: clientX - rect.left,
        y: clientY - rect.top,
      };
    };

    const start = (e) => {
      e.preventDefault();
      const { x, y } = getPos(e);
      setIsDrawing(true);
      lastX = x; lastY = y;
      ctx.beginPath();
      ctx.moveTo(x, y);
    };

    const move = (e) => {
      if (!isDrawing) return;
      const { x, y } = getPos(e);
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = strokeWidth;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineTo(x, y);
      ctx.stroke();
      lastX = x; lastY = y;
    };

    const end = () => {
      setIsDrawing(false);
    };

    canvas.addEventListener("mousedown", start);
    canvas.addEventListener("mousemove", move);
    canvas.addEventListener("mouseup", end);
    canvas.addEventListener("mouseleave", end);

    canvas.addEventListener("touchstart", start, { passive: false });
    canvas.addEventListener("touchmove", move, { passive: false });
    canvas.addEventListener("touchend", end);

    return () => {
      canvas.removeEventListener("mousedown", start);
      canvas.removeEventListener("mousemove", move);
      canvas.removeEventListener("mouseup", end);
      canvas.removeEventListener("mouseleave", end);
      canvas.removeEventListener("touchstart", start);
      canvas.removeEventListener("touchmove", move);
      canvas.removeEventListener("touchend", end);
    };
  }, [isDrawing, strokeColor, strokeWidth]);

  const clearDrawing = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const speak = () => {
    if (window.responsiveVoice) {
      window.responsiveVoice.speak(selectedLetter, "Arabic Male");
    }
  };

  return (
    <div className="p-6 md:p-10 h-full bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 relative overflow-y-auto">
      {/* Modern background elements */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(120,119,198,0.1),transparent_50%)]"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(255,119,198,0.1),transparent_50%)]"></div>
      
      <h2 className="text-3xl font-bold text-center bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 bg-clip-text text-transparent mb-6 relative z-10">Arabic Letter Tracing</h2>

      <div className="flex flex-col items-center gap-4 mb-6 relative z-10">
        <div className="flex flex-wrap justify-center gap-2">
          {letters.map((ltr) => (
            <button
              key={ltr}
              onClick={() => setSelectedLetter(ltr)}
              className={`px-4 py-2 rounded-full border text-lg font-medium transition-all duration-300 hover:scale-105 ${
                ltr === selectedLetter 
                  ? "bg-gradient-to-r from-green-500 to-emerald-500 text-white border-green-600 shadow-lg" 
                  : "bg-white/80 backdrop-blur-sm text-purple-700 border-purple-300 hover:bg-purple-50 hover:shadow-lg"
              }`}
            >
              {ltr}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3 bg-white/80 backdrop-blur-sm p-3 rounded-full border border-white/30 shadow-lg">
          <label className="text-purple-800 font-medium">Brush</label>
          <input
            type="color"
            value={strokeColor}
            onChange={(e) => setStrokeColor(e.target.value)}
            className="w-10 h-10 p-0 border rounded-full shadow-sm"
            aria-label="Pick brush color"
          />
          <input
            type="range"
            min="4"
            max="28"
            value={strokeWidth}
            onChange={(e) => setStrokeWidth(Number(e.target.value))}
            className="w-20"
            aria-label="Brush width"
          />
        </div>
      </div>

      <div className="max-w-3xl mx-auto w-full bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 border border-white/30 relative z-10">
        <div className="relative w-full">
          <canvas ref={guideRef} className="w-full rounded-md border border-purple-200" />
          <canvas ref={canvasRef} className="w-full absolute top-0 left-0 rounded-md" />
        </div>
        <div className="mt-4 flex flex-wrap gap-3 justify-center">
          <button onClick={speak} className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-5 py-2 rounded-full shadow-lg hover:scale-105 transition-all duration-300">
            🔊 Listen
          </button>
          <button onClick={clearDrawing} className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white px-5 py-2 rounded-full shadow-lg hover:scale-105 transition-all duration-300">
            🧹 Clear
          </button>
        </div>
      </div>

      <div className="mt-8 text-center text-sm text-purple-800 relative z-10">
        Trace over the faint letter. Works with mouse or touch.
      </div>

      <script src="https://code.responsivevoice.org/responsivevoice.js?key=1X64heoV"></script>
    </div>
  );
}

export default TracingGame;


